import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { sb } from '../../lib/supabase';
import { useSupabaseAuth } from '../../context/AuthContext';
import useWorkspaceAccess from '../../hooks/useWorkspaceAccess';
import Loader from '../../components/Loader';
import InitiationLayout from '../../components/InitiationLayout';
import TaxonomyTree from '../../components/TaxonomyTree';
import Badge from '../../components/Badge';
import {
  buildTree,
  flattenTree,
  descendantIds,
  findNodeInTree,
  findL1Ancestor,
  LEVEL_LABELS,
  LEVEL_BADGE_VARIANT,
} from '../../lib/taxonomy';
import { SCOPE_PIN_STATUS_LABEL, statusBadgeClass } from '../../lib/initiation';

export default function ProcessScope() {
  const { project, programme } = useOutletContext();
  const { profile } = useSupabaseAuth();
  const { canWrite } = useWorkspaceAccess();
  const orgId = programme?.organisation_id;

  const [pin, setPin] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [scopeRows, setScopeRows] = useState([]);
  const [versionLabel, setVersionLabel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);

  const locked = pin?.status === 'approved';
  const readOnly = !canWrite || locked;
  const tree = useMemo(() => buildTree(nodes), [nodes]);

  const inScopeSet = useMemo(() => {
    const set = new Set();
    for (const row of scopeRows) {
      if (row.in_scope) set.add(row.process_node_id);
    }
    return set;
  }, [scopeRows]);

  const scopeByL1 = useMemo(() => {
    const groups = new Map();
    for (const row of scopeRows.filter((r) => r.in_scope)) {
      const node = nodes.find((n) => n.id === row.process_node_id);
      if (!node) continue;
      const l1 = findL1Ancestor(tree, node.id);
      if (!l1) continue;
      if (!groups.has(l1.id)) groups.set(l1.id, { l1, nodes: [] });
      groups.get(l1.id).nodes.push(node);
    }
    return [...groups.values()];
  }, [scopeRows, nodes, tree]);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const { data: pinRow } = await sb
        .from('project_taxonomy_pins')
        .select('*, taxonomy_versions(version_label, status)')
        .eq('project_id', project.id)
        .maybeSingle();
      setPin(pinRow || null);
      setVersionLabel(pinRow?.taxonomy_versions?.version_label || null);

      if (pinRow?.taxonomy_version_id) {
        const [{ data: nodeRows }, { data: scope }] = await Promise.all([
          sb
            .from('org_process_hierarchy')
            .select('*')
            .eq('taxonomy_version_id', pinRow.taxonomy_version_id)
            .order('level')
            .order('order_index'),
          sb
            .from('project_process_scope_nodes')
            .select('*')
            .eq('project_id', project.id),
        ]);
        setNodes(nodeRows || []);
        setScopeRows(scope || []);
      } else {
        setNodes([]);
        setScopeRows([]);
      }
    } catch (e) {
      console.error('[ProcessScope] load failed', e);
      showToast('Failed to load process scope');
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => { load(); }, [load]);

  async function startScope() {
    if (!orgId || !project?.id) return;
    setSaving(true);
    try {
      const { data: published } = await sb
        .from('taxonomy_versions')
        .select('id, version_label')
        .eq('organisation_id', orgId)
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!published) {
        showToast('No published taxonomy version. Ask an admin to publish the company taxonomy.');
        return;
      }

      const { data, error } = await sb
        .from('project_taxonomy_pins')
        .insert({
          project_id: project.id,
          taxonomy_version_id: published.id,
          status: 'draft',
          created_by: profile?.id || null,
        })
        .select('*, taxonomy_versions(version_label, status)')
        .single();
      if (error) throw error;
      setPin(data);
      setVersionLabel(data.taxonomy_versions?.version_label);

      const { data: nodeRows } = await sb
        .from('org_process_hierarchy')
        .select('*')
        .eq('taxonomy_version_id', published.id)
        .order('level')
        .order('order_index');
      setNodes(nodeRows || []);
      setScopeRows([]);
      showToast('Process scope started', 'success');
    } catch (e) {
      showToast(e.message || 'Could not start scope');
    } finally {
      setSaving(false);
    }
  }

  async function setNodeScope(node, checked) {
    if (readOnly || !project?.id) return;
    setSaving(true);
    try {
      const ids = [node.id, ...descendantIds(node)];
      if (checked) {
        for (const id of ids) {
          const { error } = await sb.from('project_process_scope_nodes').upsert(
            {
              project_id: project.id,
              process_node_id: id,
              in_scope: true,
              not_applicable_reason: null,
              proposed_by: 'pm',
              created_by: profile?.id || null,
              updated_by: profile?.id || null,
            },
            { onConflict: 'project_id,process_node_id' }
          );
          if (error) throw error;
        }
      } else {
        for (const id of ids) {
          const { error } = await sb.from('project_process_scope_nodes').upsert(
            {
              project_id: project.id,
              process_node_id: id,
              in_scope: false,
              not_applicable_reason: 'Excluded from project scope',
              proposed_by: 'pm',
              updated_by: profile?.id || null,
            },
            { onConflict: 'project_id,process_node_id' }
          );
          if (error) throw error;
        }
      }
      const { data: scope } = await sb
        .from('project_process_scope_nodes')
        .select('*')
        .eq('project_id', project.id);
      setScopeRows(scope || []);
      if (pin?.status === 'empty') {
        await sb.from('project_taxonomy_pins').update({ status: 'draft' }).eq('id', pin.id);
        setPin((p) => ({ ...p, status: 'draft' }));
      }
    } catch (e) {
      showToast(e.message || 'Could not update scope');
    } finally {
      setSaving(false);
    }
  }

  async function removeFromScope(nodeId) {
    if (readOnly) return;
    const node = findNodeInTree(tree, nodeId);
    if (node) await setNodeScope(node, false);
  }

  async function submitForReview() {
    if (!pin?.id || readOnly) return;
    const inCount = scopeRows.filter((r) => r.in_scope).length;
    if (inCount === 0) {
      showToast('Select at least one in-scope process node');
      return;
    }
    setSaving(true);
    try {
      const { error } = await sb
        .from('project_taxonomy_pins')
        .update({
          status: 'review',
          submitted_at: new Date().toISOString(),
          submitted_by: profile?.id || null,
        })
        .eq('id', pin.id);
      if (error) throw error;
      setPin((p) => ({ ...p, status: 'review' }));
      showToast('Submitted for review', 'success');
    } catch (e) {
      showToast(e.message || 'Submit failed');
    } finally {
      setSaving(false);
    }
  }

  async function approveScope() {
    if (!pin?.id || readOnly) return;
    setSaving(true);
    try {
      const { error } = await sb
        .from('project_taxonomy_pins')
        .update({
          status: 'approved',
          approved_at: new Date().toISOString(),
          approved_by: profile?.id || null,
        })
        .eq('id', pin.id);
      if (error) throw error;
      setPin((p) => ({ ...p, status: 'approved' }));
      showToast('Process scope approved — taxonomy version pinned', 'success');
    } catch (e) {
      showToast(e.message || 'Approval failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Loader text="Loading process scope…" />;

  const status = pin?.status || 'empty';
  const badge = <span className={`b ${statusBadgeClass(status)}`}>{SCOPE_PIN_STATUS_LABEL[status] || status}</span>;
  const inScopeCount = scopeRows.filter((r) => r.in_scope).length;

  const actions = !canWrite ? null : !pin ? (
    <button type="button" className="btn-primary" disabled={saving} onClick={startScope}>Start process scope</button>
  ) : !locked ? (
    <>
      {pin.status === 'draft' && (
        <button type="button" className="btn-ghost" disabled={saving} onClick={submitForReview}>Submit for review</button>
      )}
      {(pin.status === 'draft' || pin.status === 'review') && (
        <button type="button" className="btn-primary" disabled={saving} onClick={approveScope}>Approve & pin version</button>
      )}
    </>
  ) : null;

  return (
    <InitiationLayout
      title="Process scope"
      subtitle={`${project?.name} · IN-00`}
      badge={badge}
      actions={actions}
    >
      {!pin ? (
        <div className="card" style={{ padding: 20 }}>
          <p style={{ color: '#5f5e5a', fontSize: 12, lineHeight: 1.6, marginBottom: 12 }}>
            Define which process areas from the company taxonomy are in scope for this project.
            A published taxonomy version will be pinned on approval.
          </p>
          {canWrite && (
            <button type="button" className="btn-primary" disabled={saving} onClick={startScope}>
              Start process scope
            </button>
          )}
        </div>
      ) : (
        <div className="scope-layout">
          <div className="tax-panel">
            <div className="tax-panel-hdr">
              <span className="tax-panel-title">Company taxonomy</span>
              <span className="tax-panel-meta">
                {versionLabel && <span className="version-pill"><span className="version-dot" />{versionLabel}</span>}
              </span>
            </div>
            <input
              className="tax-search"
              type="text"
              placeholder="Search process areas…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={readOnly}
            />
            <TaxonomyTree
              tree={tree}
              search={search}
              mode="scope"
              inScopeSet={inScopeSet}
              onToggleScope={setNodeScope}
              readOnly={readOnly || saving}
            />
          </div>

          <div className="scope-right-panel">
            <div className="coverage-summary">
              <div className="cov-title">Scope coverage</div>
              <div className="cov-stat-row">
                <div className="cov-stat">
                  <div className="cov-val">{inScopeCount}</div>
                  <div className="cov-lbl">In scope</div>
                </div>
                <div className="cov-stat">
                  <div className="cov-val">{flattenTree(tree).length - inScopeCount}</div>
                  <div className="cov-lbl">Out of scope</div>
                </div>
                <div className="cov-stat">
                  <div className="cov-val">{scopeByL1.length}</div>
                  <div className="cov-lbl">L1 areas</div>
                </div>
              </div>
            </div>

            {locked && (
              <div className="gate-banner info" style={{ marginBottom: 12 }}>
                <span>Scope approved — taxonomy version {versionLabel} is pinned. Requirements generation can proceed.</span>
              </div>
            )}

            {!locked && inScopeCount === 0 && (
              <p style={{ fontSize: 12, color: '#888780', marginBottom: 12 }}>
                Check process nodes in the taxonomy browser to add them to project scope.
              </p>
            )}

            {scopeByL1.map(({ l1, nodes: scoped }) => (
              <div key={l1.id} className="scope-card">
                <div className="scope-card-hdr">
                  <Badge variant={LEVEL_BADGE_VARIANT[1]}>{l1.code}</Badge>
                  <span className="scope-card-name">{l1.name}</span>
                  <span className="scope-card-meta">{scoped.length} node{scoped.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="scope-card-body">
                  {scoped
                    .sort((a, b) => a.level - b.level || a.code.localeCompare(b.code))
                    .map((n) => (
                      <div key={n.id} className="scope-node-row">
                        <Badge variant={LEVEL_BADGE_VARIANT[n.level]} style={{ fontSize: 9 }}>{LEVEL_LABELS[n.level]}</Badge>
                        <span className={`scope-node-label${n.level >= 3 ? ' scope-node-full' : ''}`}>{n.name}</span>
                        {!readOnly && (
                          <button type="button" className="scope-card-remove" onClick={() => removeFromScope(n.id)}>Remove</button>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {toast && (
        <div className={toast.type === 'success' ? 'toast-success' : 'toast-error'}>{toast.msg}</div>
      )}
    </InitiationLayout>
  );
}
